import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Form,
  InputGroup,
  Modal,
  Placeholder,
  Table,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFileInvoice,
  faCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { CTeDto } from "src/pages/Privada/Fiscal/CTe/CTe.Dto";
import { CTeFiltrar } from "src/services/Fiscal.Service";
import { showToast } from "src/componentes";
import { MapearErrosServidor } from "src/hook/Functions";

interface Props {
  show: boolean;
  onClose: () => void;
  codigosMunicipioRemetente: number[];
  nomesMunicipiosRemetente?: string[];
  codigoMunicipioDestinatario: number;
  nomeMunicipioDestinatario?: string;
  onImportar: (chaves: string[]) => void;
}

export const extrairChaveAcessoCTe = (item: CTeDto): string => {
  const chave =
    item.autorizacao?.chaveAcesso ||
    item.chaveAcesso ||
    (item as any).chave ||
    "";
  return chave ? chave.replace(/\D/g, "") : "";
};

const MDFeModalImportarCTeView = ({
  show,
  onClose,
  codigosMunicipioRemetente,
  nomesMunicipiosRemetente = [],
  codigoMunicipioDestinatario,
  nomeMunicipioDestinatario,
  onImportar,
}: Props) => {
  const [data, setData] = useState<CTeDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const carregarCTes = async () => {
    if (!codigoMunicipioDestinatario) {
      setData([]);
      return;
    }

    setLoading(true);
    setSelectedKeys(new Set());
    try {
      const response = await CTeFiltrar({
        codigoStatusEnvio: 2, // 2 = Finalizada / Autorizada
        codigosMunicipioRemetente:
          codigosMunicipioRemetente && codigosMunicipioRemetente.length > 0
            ? codigosMunicipioRemetente
            : undefined,
        codigoMunicipioDestinatario: codigoMunicipioDestinatario || undefined,
      });

      if (response?.data?.success) {
        setData(response.data.data || []);
      } else if (response?.data?.notifications) {
        MapearErrosServidor(response.data.notifications, showToast);
        setData([]);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Erro ao buscar CT-es para importação no MDF-e:", error);
      showToast({
        message: "Ocorreu um erro ao consultar CT-es autorizados.",
        title: "Erro",
        variant: "Danger",
      });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      setSearchTerm("");
      carregarCTes();
    }
  }, [show, codigoMunicipioDestinatario]);

  // Filtro de busca local
  const ctesFiltrados = useMemo(() => {
    if (!searchTerm.trim()) return data;
    const term = searchTerm.toLowerCase().trim();

    return data.filter((item) => {
      const numCte = String(item.numeroCte || "");
      const serie = String(item.serie || "");
      const remetente = (item.remetente?.nome || "").toLowerCase();
      const destinatario = (item.destinatario?.nome || "").toLowerCase();
      const chave = extrairChaveAcessoCTe(item);

      return (
        numCte.includes(term) ||
        serie.includes(term) ||
        remetente.includes(term) ||
        destinatario.includes(term) ||
        chave.includes(term)
      );
    });
  }, [data, searchTerm]);

  // Chaves válidas da lista filtrada
  const chavesValidasVisiveis = useMemo(() => {
    return ctesFiltrados
      .map(extrairChaveAcessoCTe)
      .filter((k) => k.length === 44);
  }, [ctesFiltrados]);

  const allVisibleSelected =
    chavesValidasVisiveis.length > 0 &&
    chavesValidasVisiveis.every((k) => selectedKeys.has(k));

  const handleToggleSelectAll = () => {
    const updated = new Set(selectedKeys);
    if (allVisibleSelected) {
      chavesValidasVisiveis.forEach((k) => updated.delete(k));
    } else {
      chavesValidasVisiveis.forEach((k) => updated.add(k));
    }
    setSelectedKeys(updated);
  };

  const handleToggleSelectKey = (chave: string) => {
    if (!chave || chave.length !== 44) return;
    const updated = new Set(selectedKeys);
    if (updated.has(chave)) {
      updated.delete(chave);
    } else {
      updated.add(chave);
    }
    setSelectedKeys(updated);
  };

  const handleConfirmarImportacao = () => {
    const chavesParaImportar = Array.from(selectedKeys);
    if (chavesParaImportar.length === 0) {
      showToast({
        message: "Selecione ao menos um CT-e para importar.",
        title: "Atenção",
        variant: "Warning",
      });
      return;
    }
    onImportar(chavesParaImportar);
    onClose();
  };

  const formatarMoeda = (valor?: number | null) => {
    return (valor ?? 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatarDataHora = (dataHora?: string | null) => {
    if (!dataHora) return "-";
    const m = moment(dataHora);
    return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "-";
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faFileInvoice} className="text-primary" />
          <span>Importar CT-es Emitidos</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-3">
        {/* Badges com informações de filtro aplicadas */}
        <div className="bg-light p-2 rounded mb-3 border">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="small fw-semibold text-secondary">
              Filtros ativos:
            </span>
            <Badge bg="primary">Status: Finalizada (2)</Badge>
            <Badge bg="info" className="text-white">
              Destino:{" "}
              {nomeMunicipioDestinatario
                ? `${nomeMunicipioDestinatario} (IBGE: ${codigoMunicipioDestinatario})`
                : `IBGE: ${codigoMunicipioDestinatario}`}
            </Badge>
            {nomesMunicipiosRemetente.length > 0 ? (
              <Badge bg="secondary">
                Carregamento: {nomesMunicipiosRemetente.join(", ")}
              </Badge>
            ) : codigosMunicipioRemetente.length > 0 ? (
              <Badge bg="secondary">
                Carregamento IBGE: {codigosMunicipioRemetente.join(", ")}
              </Badge>
            ) : null}
          </div>
        </div>

        {/* Barra de busca rápida */}
        <div className="mb-3">
          <InputGroup size="sm">
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar por número do CT-e, remetente, destinatário ou chave..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading}
            />
            {searchTerm && (
              <Button
                variant="outline-secondary"
                onClick={() => setSearchTerm("")}
              >
                Limpar
              </Button>
            )}
          </InputGroup>
        </div>

        {/* Tabela de CT-es */}
        {loading ? (
          <Placeholder animation="glow">
            <Placeholder xs={12} size="lg" className="mb-2" />
            <Placeholder xs={12} size="lg" className="mb-2" />
            <Placeholder xs={12} size="lg" className="mb-2" />
          </Placeholder>
        ) : ctesFiltrados.length === 0 ? (
          <div className="alert alert-info mb-0 text-center py-4">
            <p className="mb-1 fw-semibold">
              Nenhum CT-e autorizado encontrado para o trajeto especificado.
            </p>
            <small className="text-muted">
              Verifique se os locais de carregamento e o município de descarga
              coincidem com os CT-es emitidos e autorizados (Status 2).
            </small>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover bordered size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "40px" }} className="text-center">
                    <Form.Check
                      type="checkbox"
                      aria-label="Selecionar todos os CT-es"
                      checked={allVisibleSelected}
                      onChange={handleToggleSelectAll}
                    />
                  </th>
                  <th style={{ width: "120px" }}>Número / Série</th>
                  <th style={{ width: "140px" }}>Emissão</th>
                  <th>Remetente</th>
                  <th>Destinatário</th>
                  <th style={{ width: "150px" }} className="text-end">
                    Valor Prestação
                  </th>
                </tr>
              </thead>
              <tbody>
                {ctesFiltrados.map((item) => {
                  const chave = extrairChaveAcessoCTe(item);
                  const isValida = chave.length === 44;
                  const isChecked = selectedKeys.has(chave);

                  const remetenteNome = item.remetente?.nome || "-";
                  const remetenteCidadeUf = `${
                    item.remetente?.nomeMunicipio ||
                    item.nomeMunicipioInicio ||
                    ""
                  }${
                    item.remetente?.uf || item.ufInicio
                      ? ` - ${item.remetente?.uf || item.ufInicio}`
                      : ""
                  }`;

                  const destinatarioNome = item.destinatario?.nome || "-";
                  const destinatarioCidadeUf = `${
                    item.destinatario?.nomeMunicipio ||
                    item.nomeMunicipioFim ||
                    ""
                  }${
                    item.destinatario?.uf || item.ufFim
                      ? ` - ${item.destinatario?.uf || item.ufFim}`
                      : ""
                  }`;

                  const valor =
                    item.valoresPrestacao?.valorPrestacaoServico ??
                    item.valoresPrestacao?.valorReceber ??
                    0;

                  return (
                    <tr
                      key={item.id ?? chave ?? item.numeroCte}
                      className={isChecked ? "table-active" : undefined}
                      style={{
                        cursor: isValida ? "pointer" : "not-allowed",
                      }}
                      onClick={() => isValida && handleToggleSelectKey(chave)}
                    >
                      <td
                        className="text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Form.Check
                          type="checkbox"
                          aria-label={`Selecionar CT-e ${item.numeroCte}`}
                          checked={isChecked}
                          disabled={!isValida}
                          onChange={() => handleToggleSelectKey(chave)}
                        />
                      </td>
                      <td>
                        <span className="fw-semibold">{item.numeroCte}</span>
                        <span className="text-muted"> / {item.serie}</span>
                        {!isValida && (
                          <div>
                            <Badge bg="warning" text="dark" className="small">
                              Sem chave válida
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td className="small text-muted">
                        {formatarDataHora(item.emissaoDataHora)}
                      </td>
                      <td>
                        <div className="fw-medium text-truncate">
                          {remetenteNome}
                        </div>
                        {remetenteCidadeUf && (
                          <div className="small text-muted text-truncate">
                            {remetenteCidadeUf}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="fw-medium text-truncate">
                          {destinatarioNome}
                        </div>
                        {destinatarioCidadeUf && (
                          <div className="small text-muted text-truncate">
                            {destinatarioCidadeUf}
                          </div>
                        )}
                      </td>
                      <td className="text-end fw-semibold text-primary">
                        {formatarMoeda(valor)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between align-items-center">
        <div className="text-muted small">
          {selectedKeys.size > 0 ? (
            <span className="fw-semibold text-primary">
              {selectedKeys.size} CT-e(s) selecionado(s)
            </span>
          ) : (
            <span>Nenhum CT-e selecionado</span>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} className="me-1" />
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={selectedKeys.size === 0}
            onClick={handleConfirmarImportacao}
          >
            <FontAwesomeIcon icon={faCheck} className="me-1" />
            Importar Chaves Selecionadas ({selectedKeys.size})
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default MDFeModalImportarCTeView;
